import { useState, useEffect } from "react";
import { C, DEFAULT_PRODUCTS, SLIDES as DEFAULT_SLIDES } from "./data";
import { fetchProducts, fetchNavCats, fetchSlides, saveSlides, fetchBrandImages, saveBrandImages, fetchSiteSettings, saveSiteSettings, DEFAULT_SITE_SETTINGS, fetchInfoContent, saveInfoContent, fetchMainMenu, DEFAULT_MAIN_MENU } from "./api";
import LandingPage   from "./components/LandingPage";
import Header        from "./components/Header";
import HomePage      from "./components/HomePage";
import ShopPage      from "./components/ShopPage";
import AboutPage     from "./components/AboutPage";
import DetailPage    from "./components/DetailPage";
import AdminPage     from "./components/AdminPage";
import InfoPage      from "./components/InfoPage";
import AuthModal     from "./components/AuthModal";
import CartDrawer    from "./components/CartDrawer";
import CheckoutPage  from "./components/CheckoutPage";
import MyPage        from "./components/MyPage";
import ResetPasswordPage from "./components/ResetPasswordPage";
import SignupPage    from "./components/SignupPage";
import PaymentResultPage from "./components/PaymentResultPage";
import CouponBanner from "./components/CouponBanner";
import BoardPage from "./components/BoardPage";
import { useAuth }   from "./context/AuthContext";
import { useCart }   from "./context/CartContext";
import { supabase }  from "./supabase";

const ADMIN_PATH = process.env.REACT_APP_ADMIN_PATH || "/admin11";

const getInitialPage = () => {
  const p = window.location.pathname;
  if (p === ADMIN_PATH)              return "admin";
  if (p.startsWith("/product/"))     return "detail";
  if (p === "/shop")                 return "shop";
  if (p === "/about")                return "about";
  if (p.startsWith("/info/"))        return "info";
  if (p === "/checkout")             return "checkout";
  if (p === "/mypage")               return "mypage";
  if (p === "/reset-password")       return "resetpw";
  if (p === "/signup")               return "signup";
  if (p === "/payment/success")        return "payment-result";
  if (p === "/payment/fail")           return "payment-result";
  if (p === "/payment/naver-result")   return "payment-result";
  if (p === "/board")                return "board";
  return "landing";
};

export default function App() {
  const { user, profile, authLoading, passwordRecovery, newSocialUser, clearNewSocialUser } = useAuth();
  const { setCartOpen } = useCart();

  const [products,    setProducts]    = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [page,        setPage]        = useState(getInitialPage);
  const [landingOut,  setLandingOut]  = useState(false);
  const [cat,         setCat]         = useState("All");
  const [shopFilter,  setShopFilter]  = useState("All");
  const [infoType,    setInfoType]    = useState(() => {
    const p = window.location.pathname;
    return p.startsWith("/info/") ? p.replace("/info/", "") : "contact";
  });
  const [detailId,    setDetailId]    = useState(null);
  const [buyNowItem,  setBuyNowItem]  = useState(null);
  const [navCats,     setNavCats]     = useState(null);
  const [mainMenu,    setMainMenu]    = useState(DEFAULT_MAIN_MENU);
  const [slides,       setSlides]       = useState(DEFAULT_SLIDES);
  const [siteSettings, setSiteSettings] = useState(DEFAULT_SITE_SETTINGS);
  const [eventBannerDismissed, setEventBannerDismissed] = useState(
    () => sessionStorage.getItem('eventBannerDismissed') === '1'
  );
  const [brandImages, setBrandImages] = useState({
    brandStrip: null, aboutHero: null,
    collections: [{ img:null, label:"TRAINING" }, { img:null, label:"PERFORMANCE" }, { img:null, label:"ESSENTIALS" }],
  });
  const [infoContent,  setInfoContent]  = useState(null);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [socialSignupDone, setSocialSignupDone] = useState(false);

  const [initSlug] = useState(() => {
    const path = window.location.pathname;
    return path.startsWith("/product/") ? path.replace("/product/", "") : null;
  });

  /* ── 네이버 OAuth 콜백 처리 (authorization code flow) ── */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code  = params.get("code");
    const state = params.get("state");
    const savedState = sessionStorage.getItem("naver_state");
    if (code && state && state === savedState) {
      sessionStorage.removeItem("naver_state");
      window.history.replaceState({}, "", window.location.pathname);
      handleNaverCode(code);
    }
  }, []); // eslint-disable-line

  const handleNaverCode = async (code) => {
    try {
      // Edge Function: 코드 → 토큰 교환 + 프로필 조회 + 유저 생성/확인 + 세션 반환
      const { data, error: fnErr } = await supabase.functions.invoke("naver-auth", {
        body: { code, client_id: process.env.REACT_APP_NAVER_CLIENT_ID, redirect_uri: window.location.origin },
      });
      if (fnErr) {
        let detail = fnErr.message;
        try { const body = await fnErr.context?.json(); detail = JSON.stringify(body); } catch {}
        alert(`네이버 로그인 오류: ${detail}`);
        return;
      }
      if (!data?.session) {
        alert("네이버 로그인에 실패했습니다. 잠시 후 다시 시도해 주세요.");
        return;
      }

      const { error: sessionErr } = await supabase.auth.setSession({
        access_token: data.session.access_token,
        refresh_token: data.session.refresh_token,
      });
      if (sessionErr) {
        alert(`네이버 로그인 실패: ${sessionErr.message}`);
        return;
      }

      if (data?.is_new) setSocialSignupDone(true);
    } catch (err) {
      alert(`네이버 로그인 오류: ${err.message}`);
    }
  };

  useEffect(() => { fetchNavCats().then(setNavCats).catch(() => {}); }, []);
  useEffect(() => { fetchMainMenu().then(setMainMenu).catch(() => {}); }, []);
  useEffect(() => { fetchSlides().then(setSlides).catch(() => {}); }, []);
  useEffect(() => { fetchBrandImages().then(setBrandImages).catch(() => {}); }, []);
  useEffect(() => { fetchSiteSettings().then(setSiteSettings).catch(() => {}); }, []);
  useEffect(() => { fetchInfoContent().then(setInfoContent).catch(() => {}); }, []);

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 5000);
    fetchProducts()
      .then((data) => {
        const list = data.length > 0 ? data : DEFAULT_PRODUCTS;
        setProducts(list);
        if (initSlug) {
          const p = list.find((pr) => pr.slug === initSlug);
          if (p) setDetailId(p.id);
        }
      })
      .catch(() => {
        setProducts(DEFAULT_PRODUCTS);
      })
      .finally(() => { clearTimeout(timer); setLoading(false); });
  }, [initSlug]);

  useEffect(() => {
    const handlePop = () => {
      const path = window.location.pathname;
      if (path === ADMIN_PATH)             { setPage("admin"); return; }
      if (path.startsWith("/product/"))    {
        const slug = path.replace("/product/", "");
        const p = products.find(pr => pr.slug === slug);
        if (p) { setDetailId(p.id); setPage("detail"); return; }
      }
      if (path === "/shop")                { setPage("shop"); return; }
      if (path === "/about")               { setPage("about"); return; }
      if (path.startsWith("/info/"))       { setInfoType(path.replace("/info/", "")); setPage("info"); return; }
      if (path === "/checkout")             { setPage("checkout"); return; }
      if (path === "/mypage")               { setPage("mypage"); return; }
      if (path === "/reset-password")       { setPage("resetpw"); return; }
      if (path === "/signup")               { setPage("signup"); return; }
      if (path === "/payment/success" || path === "/payment/fail") { setPage("payment-result"); return; }
      if (path === "/board")               { setPage("board"); return; }
      setPage("home");
    };
    window.addEventListener("popstate", handlePop);
    return () => window.removeEventListener("popstate", handlePop);
  }, [products]);

  /* ── 카카오 신규 회원가입 완료 감지 ── */
  useEffect(() => {
    if (newSocialUser) {
      setSocialSignupDone(true);
      clearNewSocialUser();
    }
  }, [newSocialUser, clearNewSocialUser]);

  /* ── 비밀번호 재설정 메일 링크로 진입 시 자동 이동 ── */
  useEffect(() => {
    if (passwordRecovery && page !== "resetpw") {
      window.history.replaceState({}, "", "/reset-password");
      setPage("resetpw");
    }
  }, [passwordRecovery, page]);

  /* ── 비로그인 상태로 보호 페이지 직접 진입 시 홈으로 ── */
  useEffect(() => {
    if (authLoading) return; // 세션 확인 전에는 판단하지 않음
    if (page === "mypage" && !user) {
      window.history.replaceState({}, "", "/");
      setPage("home");
      setAuthModalOpen(true);
    }
  }, [page, user, authLoading]);

  useEffect(() => { window.scrollTo(0, 0); }, [page]);

  const enterSite = () => {
    setLandingOut(true);
    setTimeout(() => { window.history.replaceState({}, "", "/"); setPage("home"); setLandingOut(false); }, 680);
  };

  const goDetail = (id) => {
    const p = products.find(pr => pr.id === id);
    if (p) window.history.pushState({}, "", `/product/${p.slug}`);
    setDetailId(id); setPage("detail"); window.scrollTo(0, 0);
  };

  const goHome = () => { window.history.pushState({}, "", "/"); setPage("home"); window.scrollTo(0, 0); };

  const goShop = (filter) => {
    setShopFilter(filter); window.history.pushState({}, "", "/shop"); setPage("shop"); window.scrollTo(0, 0);
  };

  const goAbout  = () => { window.history.pushState({}, "", "/about");  setPage("about");  window.scrollTo(0, 0); };

  const goInfo = (type) => {
    setInfoType(type); window.history.pushState({}, "", `/info/${type}`); setPage("info"); window.scrollTo(0, 0);
  };

  const goCheckout = () => {
    setBuyNowItem(null);
    window.history.pushState({}, "", "/checkout"); setPage("checkout"); window.scrollTo(0, 0);
  };

  const goBuyNow = (item) => {
    setBuyNowItem(item);
    window.history.pushState({}, "", "/checkout"); setPage("checkout"); window.scrollTo(0, 0);
  };

  const goMyPage = () => {
    if (!user) { setAuthModalOpen(true); return; }
    window.history.pushState({}, "", "/mypage"); setPage("mypage"); window.scrollTo(0, 0);
  };

  const goBoard = () => { window.history.pushState({}, "", "/board"); setPage("board"); window.scrollTo(0, 0); };

  const goSignup = () => {
    window.history.pushState({}, "", "/signup"); setPage("signup"); window.scrollTo(0, 0);
  };

  const eventBannerVisible = siteSettings?.eventBanner?.enabled && !eventBannerDismissed;
  const handleDismissEventBanner = () => {
    sessionStorage.setItem('eventBannerDismissed', '1');
    setEventBannerDismissed(true);
  };

  const nav = { goHome, goShop, goAbout, goInfo, goCheckout, goBuyNow, footerLinks: siteSettings?.footerLinks, socialLinks: siteSettings?.socialLinks };

  const handleSetCat = (c) => { setCat(c); goHome(); }; // eslint-disable-line no-unused-vars

  const handleProductsChange = async () => {
    try {
      const data = await fetchProducts();
      setProducts(data.length > 0 ? data : DEFAULT_PRODUCTS);
    } catch {
    }
  };

  const product = products.find(p => p.id === detailId);

  if (loading) return (
    <div style={{ minHeight:"100vh", background:C.bg, display:"flex", alignItems:"center", justifyContent:"center" }}>
      <span style={{ fontFamily:"'DM Serif Display',serif", fontSize:13, letterSpacing:4, color:C.muted }}>UNDERLOOKED</span>
    </div>
  );

  return (
    <div style={{ background:C.bg, minHeight:"100vh", color:C.ink, overflowX:"hidden" }}>
      <style>{`
:root { --header-h: ${eventBannerVisible ? 96 : 60}px; --page-top: ${eventBannerVisible ? 136 : 100}px; }
*, *::before, *::after { margin:0; padding:0; box-sizing:border-box; }
        html { scroll-behavior:smooth; }
        body { background:${C.bg}; overflow-x:hidden; }
        ::selection { background:${C.accent}; color:#fff; }
        ::-webkit-scrollbar { width:2px; }
        ::-webkit-scrollbar-thumb { background:${C.border}; }

        @keyframes fadeUp   { from{opacity:0;transform:translateY(14px)} to{opacity:1;transform:translateY(0)} }
        @keyframes fadeIn   { from{opacity:0} to{opacity:1} }
        @keyframes landRise { from{opacity:0;transform:translateY(32px)} to{opacity:1;transform:translateY(0)} }
        @keyframes landDrop { from{opacity:0;transform:translateY(-12px)} to{opacity:1;transform:translateY(0)} }
        @keyframes scrollPulse { 0%,100%{opacity:.18;transform:scaleY(.6) translateY(0)} 50%{opacity:.55;transform:scaleY(1) translateY(4px)} }
        @keyframes slideInRight { from{transform:translateX(100%)} to{transform:translateX(0)} }
        @keyframes slideInUp { from{opacity:0;transform:translateY(30px)} to{opacity:1;transform:translateY(0)} }
        @keyframes slideInLeft { from{opacity:0;transform:translateX(-40px)} to{opacity:1;transform:translateX(0)} }

        .nt {
          font-family:'Pretendard',sans-serif; font-weight:500; font-size:11px; letter-spacing:2.5px;
          color:${C.ink}; cursor:pointer; text-transform:uppercase;
          transition:color .2s; background:none; border:none; padding:0; position:relative;
          line-height:1; vertical-align:middle;
        }
        .nt:hover { color:${C.muted}; }
        .nt::after { content:''; position:absolute; bottom:-2px; left:0; right:0; height:1px; background:${C.accent}; transform:scaleX(0); transform-origin:left; transition:transform .25s; }
        .nt:hover::after { transform:scaleX(1); }

        .shop-wrap { position:relative; display:flex; align-items:center; }
        .shop-wrap .dropdown-bridge { position:absolute; top:100%; left:50%; transform:translateX(-50%); width:120%; height:14px; }
        .dropdown {
          position:absolute; top:calc(100% + 14px); left:50%; transform:translateX(-50%);
          background:${C.bg}; border:1px solid ${C.border};
          padding:22px 28px; min-width:420px; display:flex; gap:40px;
          box-shadow:0 16px 48px rgba(26,26,26,.09); animation:fadeIn .18s ease; z-index:2000;
        }
        .drop-col-title { font-family:'Pretendard',sans-serif; font-weight:500; font-size:8px; letter-spacing:4px; color:${C.muted}; margin-bottom:12px; border-bottom:1px solid ${C.border}; padding-bottom:8px; text-transform:uppercase; }
        .drop-item { font-family:'Presentation5',sans-serif; font-size:13px; font-weight:300; color:${C.ink}; display:block; padding:6px 0; cursor:pointer; transition:color .15s, padding-left .15s; }
        .drop-item:hover { color:${C.accent}; padding-left:4px; }

        .hero-slide { position:absolute; inset:0; transition:opacity .9s ease; }

        .prod-card { cursor:pointer; }
        .prod-card .pimg { overflow:hidden; position:relative; }
        .prod-card .pimg-inner { transition:transform .55s cubic-bezier(.25,.46,.45,.94); width:100%; height:100%; }
        .prod-card:hover .pimg-inner { transform:scale(1.03); }

        .tl { font-family:'Pretendard',sans-serif; font-weight:500; font-size:10px; letter-spacing:3px; color:${C.ink}; text-decoration:none; text-transform:uppercase; display:inline-flex; align-items:center; gap:8px; border-bottom:1px solid ${C.border}; padding-bottom:2px; cursor:pointer; transition:border-color .2s, color .2s; }
        .tl:hover { border-color:${C.accent}; color:${C.accent}; }
        .tl-light { font-family:'Pretendard',sans-serif; font-weight:500; font-size:10px; letter-spacing:3px; color:rgba(255,255,255,0.85); text-decoration:none; text-transform:uppercase; display:inline-flex; align-items:center; gap:8px; border-bottom:1px solid rgba(255,255,255,0.35); padding-bottom:2px; cursor:pointer; transition:border-color .2s, color .2s; }
        .tl-light:hover { border-color:#fff; color:#fff; }

        .cat-btn { font-family:'Pretendard',sans-serif; font-weight:500; font-size:10px; letter-spacing:3px; text-transform:uppercase; background:none; border:none; padding:8px 16px; cursor:pointer; transition:all .2s; color:${C.muted}; border-bottom:1px solid transparent; }
        .cat-btn:hover { color:${C.ink}; }
        .cat-btn.on { color:${C.ink}; border-bottom-color:${C.ink}; }

        .sz-btn { font-family:'Pretendard',sans-serif; font-weight:500; font-size:11px; letter-spacing:2px; width:52px; height:44px; border:1px solid ${C.border}; background:transparent; color:${C.muted}; cursor:pointer; transition:all .18s; }
        .sz-btn:hover { border-color:${C.ink}; color:${C.ink}; }
        .sz-btn.on { background:${C.ink}; color:${C.bg}; border-color:${C.ink}; }

        .buy-btn { font-family:'Presentation5',sans-serif; font-weight:500; font-size:12px; letter-spacing:4px; text-transform:uppercase; border:none; cursor:pointer; padding:16px 0; width:100%; transition:opacity .2s; }
        .buy-btn:hover { opacity:.85; }

        .thumb-btn { cursor:pointer; border:1px solid transparent; transition:border-color .2s; overflow:hidden; }
        .thumb-btn.on { border-color:${C.ink}; }
        .thumb-btn:hover { border-color:${C.border}; }

        .fl { font-family:'Presentation5',sans-serif; font-size:12px; font-weight:300; color:rgba(242,241,237,0.4); display:block; padding:4px 0; text-decoration:none; transition:color .2s; cursor:pointer; }
        .fl:hover { color:${C.bg}; }

        .land-enter-btn { font-family:'Pretendard',sans-serif; font-weight:500; font-size:10px; letter-spacing:6px; text-transform:uppercase; color:rgba(242,241,237,.45); background:none; border:none; cursor:pointer; padding:10px 0; transition:color .4s, letter-spacing .4s; display:flex; align-items:center; gap:12px; }
        .land-enter-btn:hover { color:#F8F8F6; letter-spacing:8px; }

        .adm-input { width:100%; padding:10px 13px; border:1px solid #DDD; border-radius:4px; font-family:'Presentation5',sans-serif; font-size:14px; outline:none; transition:border-color .18s; background:#fff; color:#1A1A1A; }
        .adm-input:focus { border-color:${C.accent}; }
        .adm-btn { font-family:'Presentation5',sans-serif; font-size:13px; font-weight:500; letter-spacing:0; text-transform:none; border:none; cursor:pointer; padding:10px 20px; border-radius:3px; transition:opacity .18s; }
        .adm-btn:hover { opacity:.82; }
        .adm-label { font-family:'Presentation5',sans-serif; font-size:12px; font-weight:500; letter-spacing:0; text-transform:none; color:#444; display:block; margin-bottom:6px; }

        @media(max-width:960px) {
          .nav-desktop { display:none !important; }
          .hamburger   { display:flex !important; }
          .prod-grid   { grid-template-columns:1fr 1fr !important; }
          .detail-grid { grid-template-columns:1fr !important; }
          .footer-cols { grid-template-columns:1fr 1fr !important; }
          .checkout-grid { grid-template-columns:1fr !important; }
          .field-row { grid-template-columns:1fr !important; }
        }
        @media(max-width:560px) {
          .prod-grid { grid-template-columns:1fr !important; }
        }

        @media(max-width:768px) {
          .about-hero-grid   { grid-template-columns:1fr !important; min-height:auto !important; }
          .about-hero-text   { padding:48px 20px 36px !important; }
          .about-story-sec   { padding:52px 20px !important; }
          .about-values-sec  { padding:52px 20px !important; }
          .about-values-grid { grid-template-columns:1fr !important; gap:32px !important; }
          .about-col-sec     { padding:52px 20px 0 !important; }
          .about-col-grid    { grid-template-columns:1fr 1fr !important; }
          .about-cta-inner   { padding:20px 16px !important; flex-wrap:wrap !important; gap:10px !important; }
          .about-info-sec    { padding:52px 20px !important; }
          .about-info-grid   { grid-template-columns:1fr 1fr !important; gap:20px !important; }
        }
        @media(max-width:400px) {
          .about-col-grid  { grid-template-columns:1fr !important; }
          .about-info-grid { grid-template-columns:1fr !important; }
        }
      `}</style>

      {/* ── 인증 모달 ── */}
      {authModalOpen && <AuthModal onClose={() => setAuthModalOpen(false)} onGoSignup={goSignup} />}

      {/* ── 소셜 회원가입 완료 모달 ── */}
      {socialSignupDone && (
        <div
          onClick={() => setSocialSignupDone(false)}
          style={{
            position: "fixed", inset: 0, zIndex: 9000,
            background: "rgba(26,26,26,0.55)", backdropFilter: "blur(4px)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: C.bg, border: `1px solid ${C.border}`,
              padding: "52px 44px 44px", maxWidth: 400, width: "90%",
              textAlign: "center", animation: "fadeUp .28s ease",
            }}
          >
            <div style={{ fontSize: 32, marginBottom: 20 }}>✓</div>
            <p style={{
              fontFamily: "'Pretendard',sans-serif", fontWeight: 600,
              fontSize: 16, letterSpacing: 1, color: C.ink, marginBottom: 12,
            }}>
              회원가입이 완료됐습니다
            </p>
            <p style={{
              fontFamily: "'Pretendard',sans-serif", fontSize: 13,
              color: C.muted, lineHeight: 1.8, marginBottom: 32,
            }}>
              UNDERLOOKED 회원이 되신 것을 환영합니다.<br />
              지금 바로 쇼핑을 즐겨보세요.
            </p>
            <button
              onClick={() => setSocialSignupDone(false)}
              style={{
                fontFamily: "'Pretendard',sans-serif", fontWeight: 500,
                fontSize: 11, letterSpacing: 3, textTransform: "uppercase",
                background: C.ink, color: C.bg, border: "none",
                padding: "14px 40px", cursor: "pointer", width: "100%",
                transition: "opacity .2s",
              }}
              onMouseEnter={e => e.currentTarget.style.opacity = ".8"}
              onMouseLeave={e => e.currentTarget.style.opacity = "1"}
            >
              쇼핑 시작하기
            </button>
          </div>
        </div>
      )}

      {/* ── 장바구니 드로어 ── */}
      <CartDrawer onCheckout={goCheckout} />

      {/* ── 쿠폰 배너 ── */}
      <CouponBanner couponBanner={siteSettings?.couponBanner} user={user} onGoHome={goHome} onOpenAuth={() => setAuthModalOpen(true)} />

      {/* ── 랜딩 ── */}
      {page === "landing" && (
        <LandingPage onEnter={enterSite} isExiting={landingOut} />
      )}

      {/* ── 헤더 ── */}
      {(page === "home" || page === "detail" || page === "shop" || page === "about" || page === "info" || page === "checkout" || page === "mypage" || page === "signup" || page === "board") && (
        <Header
          onGoHome={goHome} onGoShop={goShop} onGoAbout={goAbout} onGoBoard={goBoard}
          transparent={page === "home"} navCats={navCats} mainMenu={mainMenu}
          user={user}
          onOpenAuth={() => setAuthModalOpen(true)}
          onOpenCart={() => setCartOpen(true)}
          onGoMyPage={goMyPage}
          eventBanner={siteSettings?.eventBanner}
          eventBannerVisible={eventBannerVisible}
          onDismissEventBanner={handleDismissEventBanner}
        />
      )}

      {page === "home" && (
        <HomePage products={products} cat={cat} onSetCat={setCat} onGoDetail={goDetail} nav={nav} navCats={navCats} slides={slides} brandImages={brandImages} siteSettings={siteSettings} />
      )}

      {page === "detail" && product && (
        <DetailPage product={product} allProducts={products} onGoDetail={goDetail} onGoHome={goHome} nav={nav} siteSettings={siteSettings} />
      )}

      {page === "shop" && (
        <ShopPage products={products} filter={shopFilter} onGoDetail={goDetail} onSetFilter={setShopFilter} nav={nav} navCats={navCats} />
      )}

      {page === "about" && (
        <AboutPage nav={nav} aboutImages={brandImages} />
      )}

      {page === "info" && (
        <InfoPage type={infoType} nav={nav} infoContent={infoContent} />
      )}

      {page === "checkout" && (
        <CheckoutPage onGoHome={goHome} nav={nav} onLogin={() => setAuthModalOpen(true)}
          buyNowItem={buyNowItem} onClearBuyNow={() => setBuyNowItem(null)} siteSettings={siteSettings} />
      )}

      {page === "mypage" && (
        <MyPage onGoHome={goHome} nav={nav} />
      )}

      {page === "resetpw" && (
        <ResetPasswordPage onGoHome={goHome} />
      )}

      {page === "signup" && (
        <SignupPage onGoHome={goHome} onGoLogin={() => { goHome(); setAuthModalOpen(true); }} nav={nav} />
      )}

      {page === "payment-result" && (
        <PaymentResultPage onGoHome={goHome} />
      )}

      {page === "board" && (
        <BoardPage nav={nav} onLogin={() => setAuthModalOpen(true)} />
      )}

      {page === "admin" && (
        authLoading ? (
          <div style={{ minHeight:"100vh", background:C.bg, display:"flex", alignItems:"center", justifyContent:"center" }}>
            <span style={{ fontFamily:"'DM Serif Display',serif", fontSize:13, letterSpacing:4, color:C.muted }}>UNDERLOOKED</span>
          </div>
        ) : profile?.is_admin ? (
          <AdminPage
            products={products}
            onProductsChange={handleProductsChange}
            onGoHome={goHome}
            navCats={navCats}
            onNavCatsChange={setNavCats}
            mainMenu={mainMenu}
            onMainMenuChange={setMainMenu}
            slides={slides}
            onSlidesChange={(updated) => { setSlides(updated); }}
            saveSlides={saveSlides}
            brandImages={brandImages}
            onBrandImagesChange={setBrandImages}
            saveBrandImages={saveBrandImages}
            siteSettings={siteSettings}
            onSiteSettingsChange={setSiteSettings}
            saveSiteSettings={saveSiteSettings}
            infoContent={infoContent}
            onInfoContentChange={setInfoContent}
            saveInfoContent={saveInfoContent}
          />
        ) : (
          <div style={{ minHeight:"100vh", background:C.bg, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:16 }}>
            <p style={{ fontFamily:"'Presentation5',sans-serif", fontSize:11, letterSpacing:4, color:C.muted }}>ACCESS DENIED</p>
            <button onClick={goHome} style={{ fontFamily:"'Pretendard',sans-serif", fontSize:12, color:C.muted, background:"none", border:"none", cursor:"pointer", textDecoration:"underline" }}>홈으로</button>
          </div>
        )
      )}
    </div>
  );
}
